import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, InputLabel, makeStyles, MenuItem, Select, TextField } from '@material-ui/core'
import { hasPermission } from '../../../lib/components/form/RestrictedComponent'
import deepEqual from 'deep-equal'

const useStyles = makeStyles(theme => ({
  accountDialogContent: {
    display: 'flex',
    flexDirection: 'column',
    '& > *': {
      // give components spacing
      marginBottom: '1rem'
    },
    '&:first-child': {
      marginTop: '1rem'
    }
  },
  dialogTitle: {
    backgroundColor: 'lightgray',
    '& > *': {
      fontWeight: 'bold'
    }
  },
  checkboxControl: {
    width: 'max-content'
  }
}))

const getDefaultUser = () => ({
  firstName: '',
  lastName: '',
  email: '',
  userName: '',
  userRole: 1,
  authenticationType: 1,
  recieveEmails: false,
  recieveSMS: false,
  locked: false
})

const types = {
  text: 'text',
  select: 'select',
  check: 'check'
}

const authOptions = [
  { value: "0", text: "Select One" },
  { value: "1", text: "Email & Password" },
  { value: "3", text: "LDAP Authentication" }
]

const isValidEmail = email => {
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

const AccountDialog = props => {
  const { onClose, onSubmit, isOpen, editing } = props
  const classes = useStyles()

  const [tempEditing, setTempEditing] = useState(editing || getDefaultUser())
  const securityRoles = useSelector(state => state.data.securityRoles)
  const currentUser = useSelector(state => state.data.user)
  const clientID = currentUser.clientId

  const isThisUser = !!tempEditing && tempEditing?.userId === currentUser.userId
  const isNewUser = editing === undefined
  const controlMap = [
    { key: 'firstName', label: 'First Name', type: types.text },
    { key: 'lastName', label: 'Last Name', type: types.text },
    { key: 'email', disabled: !isNewUser, label: 'Email', type: types.text, valid: isValidEmail },
    { key: 'userRole', disabled: !hasPermission('account.any_update') || isThisUser, label: 'Role', type: types.select, options: securityRoles.map(role => ({ value: role.roleID, text: role.roleName })) },
    { key: 'authenticationType', disabled: true, label: 'Authentication Method', type: types.select, options: authOptions },
    { key: 'recieveEmails', label: 'Receive Emails', type: types.check }
  ]

  const handleChange = (key, value) => {
    const newEdits = { ...tempEditing }
    if (key === 'email') {
      // username === email
      newEdits.userName = value
    }
    newEdits[key] = value
    setTempEditing(newEdits)
  }

  const isValid = user => {
    return user.firstName &&
      user.lastName &&
      user.email && isValidEmail(user.email)
  }

  return <Dialog onClose={onClose} open={isOpen} fullWidth={true} maxWidth="md">
    <DialogTitle className={classes.dialogTitle}>
      {isNewUser ? 'Create User' : 'Edit User'}
    </DialogTitle>
    <DialogContent className={classes.accountDialogContent}>
      {controlMap.map((control, i) => {
        switch (control.type) {
          case types.text:
            return <TextField
              required
              style={i === 0 ? { marginTop: '1rem' } : {}}
              error={!tempEditing[control.key] || (control.valid && !control.valid(tempEditing[control.key]))}
              key={'account control ' + i}
              variant="outlined"
              disabled={control.disabled}
              value={tempEditing[control.key]}
              label={control.label}
              onChange={ev => handleChange(control.key, ev.target.value)}
            />
          case types.select:
            return (
              <FormControl key={'account control ' + i} disabled={control.disabled}>
                <InputLabel>{control.label}</InputLabel>
                <Select
                  label={control.label}
                  value={tempEditing[control.key]}
                  onChange={ev => handleChange(control.key, ev.target.value)}
                >
                  {control.options.map(option => {
                    return <MenuItem key={'control' + i + ' option:' + option.text} value={option.value}>{option.text}</MenuItem>
                  })}
                </Select>
              </FormControl>
            )
          case types.check:
            return <FormControlLabel
              className={classes.checkboxControl}
              key={'account control ' + i}
              checked={tempEditing[control.key]}
              disabled={control.disabled}
              onChange={ev => handleChange(control.key, ev.target.checked)}
              control={<Checkbox color="primary" />}
              label={control.label}
              labelPlacement="start"
            />
        }
      })}
    </DialogContent>
    <DialogActions>
      <Button variant="contained" color="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          onSubmit({ ...tempEditing, clientID })
          onClose()
        }}
        disabled={
          !isValid(tempEditing) ||
          (!hasPermission('account.any_update') && !isThisUser) ||
          deepEqual(tempEditing, editing)
        }
      >
        Save
      </Button>
    </DialogActions>
  </Dialog >

}

export default AccountDialog
